import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthSession } from './types';
import { loadSession, saveSession, clearSession } from './tokenStorage';
import { setSessionExpiredHandler } from './apiFetch';
import * as authApi from './authApi';

interface AuthContextValue {
  session: AuthSession | null;
  loginNotice: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  changePassword: (oldPassword: string | null, newPassword: string) => Promise<void>;
  updateSession: (session: AuthSession) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<AuthSession | null>(() => loadSession());
  const [loginNotice, setLoginNotice] = useState<string | null>(null);

  useEffect(() => {
    setSessionExpiredHandler(() => {
      setLoginNotice('Session expired. Please sign in again.');
      setSession(null);
    });
    return () => setSessionExpiredHandler(null);
  }, []);

  useEffect(() => {
    if (!loginNotice) return;
    const timer = setTimeout(() => setLoginNotice(null), 5000);
    return () => clearTimeout(timer);
  }, [loginNotice]);

  const login = useCallback(async (username: string, password: string) => {
    setLoginNotice(null);
    const s = await authApi.login(username, password);
    setSession(s);
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout();
    setSession(null);
  }, []);

  const changePassword = useCallback(async (oldPassword: string | null, newPassword: string) => {
    const s = await authApi.changePassword(oldPassword, newPassword);
    setSession(s);
  }, []);

  const updateSession = useCallback((s: AuthSession) => {
    saveSession(s);
    setSession(s);
  }, []);

  const value = useMemo(
    () => ({ session, loginNotice, login, logout, changePassword, updateSession }),
    [session, loginNotice, login, logout, changePassword, updateSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
